import React, { useState, useEffect, useCallback } from "react";
import { Button, FormGroup, Input, Spinner, Badge } from "reactstrap";
import { toast } from "react-toastify";
import { UserPlus, X, Shield, Home, Monitor, Globe } from "react-feather";
import { userService }   from "../../../services/userService";
import { siteService }   from "../../../services/siteService";
import { roomService }   from "../../../services/roomService";
import { deviceService } from "../../../services/deviceService";
import ConfirmDeleteDialog from "../../Common/ConfirmDeleteDialog";

// ─── Design tokens — match RoomTree ──────────────────────────────────────────
const T = {
  textPrimary:   "#1e293b",
  textSecondary: "#64748b",
  bgLevel0:      "#f8fafc",
  bgHover:       "#f1f5f9",
  connector:     "#cbd5e0",
  iconSite:      "#8b5cf6",   // purple  — visual distinction from rooms
  iconRoom:      "#3b82f6",   // blue    — matches RoomTree
  iconDevice:    "#10b981",   // green
  iconDisabled:  "#94a3b8",
  badgeBg:       "#f1f5f9",
};

// ─── Grant level badge ────────────────────────────────────────────────────────
const BADGE_CONFIG = {
  SITE:   { color: "primary",  label: "SITE" },
  ROOM:   { color: "warning",  label: "ROOM",   textClass: "text-dark" },
  DEVICE: { color: "success",  label: "DEVICE" },
};
function GrantBadge({ level }) {
  const { color, label, textClass = "" } = BADGE_CONFIG[level] ?? BADGE_CONFIG.DEVICE;
  return (
    <Badge color={color} className={`${textClass} me-1`} style={{ fontSize: "0.68rem" }}>
      {level ? label : "—"}
    </Badge>
  );
}

// ─── Tree checkbox row ────────────────────────────────────────────────────────
function TreeRow({ icon: Icon, label, sublabel, checked, disabled, indeterminate, onChange, indent, iconColor }) {
  const ref = React.useRef(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = !!indeterminate;
  }, [indeterminate]);

  const isLevel0 = indent === 0;

  return (
    <div
      style={{
        paddingLeft:    `${indent * 28 + 10}px`,
        paddingRight:   "12px",
        paddingTop:     "7px",
        paddingBottom:  "7px",
        display:        "flex",
        alignItems:     "center",
        backgroundColor: isLevel0 ? T.bgLevel0 : "transparent",
        borderRadius:   isLevel0 ? "6px" : "0",
        marginBottom:   "2px",
        transition:     "background-color 0.15s",
        cursor:         disabled ? "not-allowed" : "pointer",
        opacity:        disabled ? 0.75 : 1,
      }}
      onMouseEnter={(e) => { if (!isLevel0) e.currentTarget.style.backgroundColor = T.bgHover; }}
      onMouseLeave={(e) => { if (!isLevel0) e.currentTarget.style.backgroundColor = "transparent"; }}
      onClick={() => !disabled && onChange && onChange()}
    >
      {/* Connector line */}
      {indent > 0 && (
        <div style={{
          position: "absolute",
          left:    `${(indent - 1) * 28 + 10 + 12}px`,
          width:   "12px",
          height:  "1px",
          backgroundColor: T.connector,
          marginTop: "0",
        }} />
      )}

      {/* Checkbox */}
      <input
        ref={ref}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => { e.stopPropagation(); if (!disabled && onChange) onChange(); }}
        onClick={(e) => e.stopPropagation()}
        style={{
          width:       "15px",
          height:      "15px",
          marginRight: "8px",
          marginTop:   0,
          flexShrink:  0,
          cursor:      disabled ? "not-allowed" : "pointer",
          accentColor: "#3b82f6",
          border:      "1.5px solid #94a3b8",
          borderRadius:"3px",
        }}
      />

      {/* Icon badge */}
      <div style={{
        width:           "22px",
        height:          "22px",
        display:         "flex",
        alignItems:      "center",
        justifyContent:  "center",
        borderRadius:    "4px",
        backgroundColor: disabled ? T.iconDisabled : iconColor,
        color:           "white",
        marginRight:     "8px",
        flexShrink:      0,
      }}>
        <Icon size={11} />
      </div>

      {/* Label */}
      <span style={{ fontSize: "0.88rem", fontWeight: indent === 0 ? 600 : 500, color: T.textPrimary, flex: 1 }}>
        {label}
      </span>

      {/* Sublabel */}
      {sublabel && (
        <span style={{ fontSize: "0.75rem", color: T.textSecondary, backgroundColor: T.badgeBg, padding: "1px 6px", borderRadius: "4px", fontFamily: "monospace" }}>
          {sublabel}
        </span>
      )}
    </div>
  );
}

// ─── Main ────────────────────────────────────────────────────────────────────
const SitePermissionManager = ({ site, siteDetails, onRefresh }) => {

  const [users,          setUsers]          = useState([]);
  const [loadingUsers,   setLoadingUsers]   = useState(false);
  const [userSearch,     setUserSearch]     = useState("");
  const [selectedUser,   setSelectedUser]   = useState("");

  const [siteChecked,    setSiteChecked]    = useState(false);
  const [checkedRooms,   setCheckedRooms]   = useState(new Set());
  const [checkedDevices, setCheckedDevices] = useState(new Set());

  const [saving,         setSaving]         = useState(false);

  const [effectiveUsers,    setEffectiveUsers]    = useState([]);
  const [loadingEffective,  setLoadingEffective]  = useState(false);

  const [revoking,          setRevoking]          = useState(null);
  const [revokingLoading,   setRevokingLoading]   = useState(false);

  // ── Derived flat lists ──────────────────────────────────────────────────────
  const allRooms      = siteDetails?.rooms ?? [];
  const allDevicesFlat = allRooms.flatMap((r) =>
    (r.devices ?? []).map((d) => ({ ...d, roomId: r.id }))
  );

  // ── Checkbox logic ──────────────────────────────────────────────────────────
  const roomChecked   = (roomId)             => siteChecked || checkedRooms.has(roomId);
  const roomDisabled  = ()                   => siteChecked;
  const devChecked    = (devId, roomId)      => roomChecked(roomId) || checkedDevices.has(devId);
  const devDisabled   = (roomId)             => roomChecked(roomId);
  const roomIndet     = (room)               => {
    if (roomChecked(room.id)) return false;
    const ids   = (room.devices ?? []).map((d) => d.id);
    const count = ids.filter((id) => checkedDevices.has(id)).length;
    return count > 0 && count < ids.length;
  };

  // ── Fetch helpers ───────────────────────────────────────────────────────────
  const fetchUsers = useCallback(async (search = "") => {
    setLoadingUsers(true);
    try {
      const res = await userService.list({ userType: "USER", ...(search.trim() && { name: search.trim() }) });
      setUsers(res.content ?? []);
    } catch { toast.error("Failed to load users"); }
    finally  { setLoadingUsers(false); }
  }, []);

  const fetchEffective = useCallback(async () => {
    if (!site?.id) return;
    setLoadingEffective(true);
    try {
      const res = await siteService.getEffectiveUsers(site.id);
      setEffectiveUsers(res ?? []);
    } catch { toast.error("Failed to load access list"); }
    finally  { setLoadingEffective(false); }
  }, [site?.id]);

  useEffect(() => { fetchUsers(); fetchEffective(); }, [fetchUsers, fetchEffective]); // eslint-disable-line

  // ── Handlers ────────────────────────────────────────────────────────────────
  const toggleSite = () => {
    const next = !siteChecked;
    setSiteChecked(next);
    if (next) { setCheckedRooms(new Set()); setCheckedDevices(new Set()); }
  };

  const toggleRoom = (roomId) => {
    const nextR = new Set(checkedRooms);
    if (nextR.has(roomId)) {
      nextR.delete(roomId);
      const nextD = new Set(checkedDevices);
      allDevicesFlat.filter((d) => d.roomId === roomId).forEach((d) => nextD.delete(d.id));
      setCheckedDevices(nextD);
    } else {
      nextR.add(roomId);
    }
    setCheckedRooms(nextR);
  };

  const toggleDevice = (deviceId) => {
    const next = new Set(checkedDevices);
    next.has(deviceId) ? next.delete(deviceId) : next.add(deviceId);
    setCheckedDevices(next);
  };

  const nothingSelected = !siteChecked && checkedRooms.size === 0 && checkedDevices.size === 0;

  const handleGrant = async () => {
    if (!selectedUser)  { toast.error("Select a user first"); return; }
    if (nothingSelected){ toast.error("Select at least one resource in the tree"); return; }

    setSaving(true);
    try {
      if (siteChecked) {
        await siteService.assignUser({ userName: selectedUser, siteId: site.id });
      } else {
        const calls = [];
        checkedRooms.forEach((roomId) =>
          calls.push(roomService.assignUser({ userName: selectedUser, roomId }))
        );
        checkedDevices.forEach((deviceId) => {
          const dev = allDevicesFlat.find((d) => d.id === deviceId);
          if (!dev || !checkedRooms.has(dev.roomId))
            calls.push(deviceService.assignUser({ userName: selectedUser, deviceId }));
        });
        await Promise.all(calls);
      }
      toast.success("Access granted");
      setSelectedUser(""); setSiteChecked(false); setCheckedRooms(new Set()); setCheckedDevices(new Set());
      await fetchEffective();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Failed to grant access");
    } finally {
      setSaving(false);
    }
  };

  const handleConfirmRevoke = async () => {
    if (!revoking) return;
    setRevokingLoading(true);
    try {
      const { userName, grantLevel, resourceId } = revoking;
      if (grantLevel === "SITE")   await siteService.unassignUser({ userName, siteId: resourceId });
      else if (grantLevel === "ROOM")  await roomService.unassignUser({ userName, roomId: resourceId });
      else                             await deviceService.unassignUser({ userName, deviceId: resourceId });
      toast.success("Access revoked");
      setRevoking(null);
      await fetchEffective();
      if (onRefresh) onRefresh();
    } catch (e) {
      toast.error(e.response?.data?.message ?? "Failed to revoke");
    } finally {
      setRevokingLoading(false);
    }
  };

  const assignableUsers = users.filter(
    (u) => !effectiveUsers.some((e) => e.userName === u.userName)
  );

  // devices in checked rooms (auto-covered by room grant)
  const devicesInCheckedRooms = allDevicesFlat.filter((d) => checkedRooms.has(d.roomId)).length;
  // devices explicitly checked but not already covered by a room grant
  const directDeviceCount = [...checkedDevices].filter((id) => {
    const d = allDevicesFlat.find((x) => x.id === id);
    return d && !checkedRooms.has(d.roomId);
  }).length;
  const totalDeviceCount = devicesInCheckedRooms + directDeviceCount;

  // ─── Render ──────────────────────────────────────────────────────────────────
  return (
    <div style={{ position: "relative" }}>

      {/* ── Section 1: Grant Access ─────────────────────────────────────────── */}
      <div style={{ marginBottom: "24px" }}>
        <p style={{ fontSize: "0.82rem", color: T.textSecondary, marginBottom: "12px" }}>
          Select a user and check the resources they should access. Checking a room
          auto-grants all its devices; checking the site grants everything.
        </p>

        {/* User search */}
        <FormGroup style={{ marginBottom: "6px" }}>
          <Input
            type="text"
            placeholder="Search users by name…"
            value={userSearch}
            bsSize="sm"
            onChange={(e) => { setUserSearch(e.target.value); fetchUsers(e.target.value); }}
          />
        </FormGroup>

        {/* User select */}
        <FormGroup style={{ marginBottom: "12px" }}>
          <Input
            type="select"
            value={selectedUser}
            onChange={(e) => setSelectedUser(e.target.value)}
            disabled={loadingUsers}
            bsSize="sm"
            size="4"
          >
            <option value="">{loadingUsers ? "Loading…" : "— select a user —"}</option>
            {assignableUsers.map((u) => (
              <option key={u.id} value={u.userName}>
                {u.userName} — {u.email}
                {!u.active && " (Inactive)"}
                {!u.verified && " (Unverified)"}
              </option>
            ))}
          </Input>
          {!loadingUsers && assignableUsers.length === 0 && userSearch && (
            <small style={{ color: T.textSecondary }}>No unassigned users matching "{userSearch}"</small>
          )}
        </FormGroup>

        {/* Hierarchy tree */}
        <div style={{ fontSize: "0.8rem", fontWeight: 600, color: T.textSecondary, marginBottom: "6px", textTransform: "uppercase", letterSpacing: "0.04em" }}>
          Access scope
        </div>
        <div
          style={{
            border:          "1px solid #e2e8f0",
            borderRadius:    "8px",
            backgroundColor: "#ffffff",
            padding:         "8px",
            maxHeight:       "300px",
            overflowY:       "auto",
            marginBottom:    "10px",
            position:        "relative",
          }}
        >
          {/* Site row */}
          <TreeRow
            icon={Globe}
            label={site?.name ?? "Site"}
            sublabel="full access"
            checked={siteChecked}
            disabled={false}
            onChange={toggleSite}
            indent={0}
            iconColor={T.iconSite}
          />

          {allRooms.length === 0 && (
            <div style={{ paddingLeft: "38px", paddingTop: "4px", fontSize: "0.82rem", color: T.textSecondary }}>
              No rooms in this site yet.
            </div>
          )}

          {allRooms.map((room) => (
            <React.Fragment key={room.id}>
              {/* Room row */}
              <TreeRow
                icon={Home}
                label={room.name}
                sublabel={`#${room.id}`}
                checked={roomChecked(room.id)}
                disabled={roomDisabled()}
                indeterminate={roomIndet(room)}
                onChange={() => toggleRoom(room.id)}
                indent={1}
                iconColor={T.iconRoom}
              />

              {/* Device rows */}
              {(room.devices ?? []).map((device) => (
                <TreeRow
                  key={device.id}
                  icon={Monitor}
                  label={device.name}
                  checked={devChecked(device.id, room.id)}
                  disabled={devDisabled(room.id)}
                  onChange={() => toggleDevice(device.id)}
                  indent={2}
                  iconColor={T.iconDevice}
                />
              ))}
            </React.Fragment>
          ))}
        </div>

        {/* Summary + button */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span style={{ fontSize: "0.8rem", color: T.textSecondary }}>
            {siteChecked
              ? "Full site access — all rooms and devices"
              : nothingSelected
              ? "Nothing selected"
              : `${checkedRooms.size} room${checkedRooms.size !== 1 ? "s" : ""}, ${totalDeviceCount} device${totalDeviceCount !== 1 ? "s" : ""}`}
          </span>
          <Button
            color="primary"
            size="sm"
            onClick={handleGrant}
            disabled={saving || !selectedUser || nothingSelected}
          >
            {saving
              ? <><Spinner size="sm" className="me-1" />Granting…</>
              : <><Shield size={13} className="me-1" />Grant Access</>}
          </Button>
        </div>
      </div>

      {/* ── Section divider ──────────────────────────────────────────────────── */}
      <hr style={{ borderColor: "#e2e8f0", margin: "0 0 16px 0" }} />

      {/* ── Section 2: Who has access ────────────────────────────────────────── */}
      <div>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
          <span style={{ fontSize: "0.95rem", fontWeight: 600, color: T.textPrimary }}>
            Who has access
            {effectiveUsers.length > 0 && (
              <span style={{ fontSize: "0.8rem", color: T.textSecondary, fontWeight: 400, marginLeft: "6px" }}>
                ({effectiveUsers.length})
              </span>
            )}
          </span>
          <div style={{ display: "flex", gap: "4px" }}>
            <GrantBadge level="SITE" />
            <GrantBadge level="ROOM" />
            <GrantBadge level="DEVICE" />
          </div>
        </div>

        {loadingEffective ? (
          <div style={{ textAlign: "center", padding: "16px", color: T.textSecondary }}>
            <Spinner size="sm" /><span style={{ marginLeft: "8px", fontSize: "0.85rem" }}>Loading…</span>
          </div>
        ) : effectiveUsers.length === 0 ? (
          <div style={{ textAlign: "center", padding: "20px", color: T.textSecondary, border: "1px dashed #e2e8f0", borderRadius: "8px", fontSize: "0.85rem" }}>
            <UserPlus size={28} style={{ opacity: 0.3, display: "block", margin: "0 auto 8px" }} />
            No users have access to this site yet.
          </div>
        ) : (
          <div style={{ border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden", maxHeight: "300px", overflowY: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ backgroundColor: T.bgLevel0, borderBottom: "1px solid #e2e8f0" }}>
                  {["User", "Level", "Resource", "Granted by", "Granted at", ""].map((h) => (
                    <th key={h} style={{ padding: "8px 10px", fontSize: "0.78rem", fontWeight: 600, color: T.textSecondary, textAlign: h === "" ? "center" : "left", whiteSpace: "nowrap" }}>
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {effectiveUsers.map((entry, idx) => (
                  <tr
                    key={idx}
                    style={{ borderBottom: "1px solid #f1f5f9", backgroundColor: idx % 2 === 0 ? "#ffffff" : "#fafafa" }}
                  >
                    <td style={{ padding: "7px 10px", fontSize: "0.83rem", fontWeight: 500, color: T.textPrimary }}>{entry.userName}</td>
                    <td style={{ padding: "7px 10px" }}><GrantBadge level={entry.grantLevel} /></td>
                    <td style={{ padding: "7px 10px", fontSize: "0.83rem", color: T.textSecondary }}>{entry.resourceName}</td>
                    <td style={{ padding: "7px 10px", fontSize: "0.83rem", color: T.textSecondary }}>{entry.grantedBy ?? "—"}</td>
                    <td style={{ padding: "7px 10px", fontSize: "0.78rem", color: T.textSecondary, whiteSpace: "nowrap" }}>
                      {entry.grantedAt ? new Date(entry.grantedAt).toLocaleString() : "—"}
                    </td>
                    <td style={{ padding: "7px 10px", textAlign: "center" }}>
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={() => setRevoking(entry)}
                        style={{ padding: "2px 8px", fontSize: "0.72rem" }}
                      >
                        <X size={11} className="me-1" />Revoke
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Revoke confirmation ──────────────────────────────────────────────── */}
      <ConfirmDeleteDialog
        isOpen={!!revoking}
        headerText="Confirm Revoke"
        title="Revoke Access"
        subtitle="This action removes the permission entry"
        icon={X}
        bodyText={
          <>
            Remove <GrantBadge level={revoking?.grantLevel} /> access for{" "}
            <strong>{revoking?.userName}</strong> on{" "}
            <strong>{revoking?.resourceName}</strong>?
          </>
        }
        warningText={
          revoking?.grantLevel === "SITE"
            ? "User loses access to all rooms and devices in this site."
            : revoking?.grantLevel === "ROOM"
            ? "User loses access to all devices in this room."
            : "User loses access to this device only."
        }
        warningLabel="Effect"
        actionText="Revoke Access"
        loadingText="Revoking…"
        onClose={() => setRevoking(null)}
        onConfirm={handleConfirmRevoke}
        loading={revokingLoading}
      />
    </div>
  );
};

export default SitePermissionManager;
